<template>
  <div>
    <!-- 제목 -->
    <v-sheet outlined class="d-flex align-center ma-2 px-4" height="60">
      <font class="font-weight-bold text-h6 grey--text text--darken-2">공주무도회 신청 리스트</font>
    </v-sheet>

    <!-- 본문 -->
    <v-sheet outlined class="pa-2 ma-2">
      <!-- 검색 -->
      <v-sheet outlined class="pa-2 mb-2 d-flex">
        <v-sheet class="d-flex" width="500">
          <v-text-field placeholder="이름, 이메일, 메신저 ID로 검색" outlined dense hide-details v-model="keyword" @keyup.enter="search()"></v-text-field>
          <v-btn height="40" class="px-6 ml-3" color="primary" dark depressed @click="search()">
            <v-icon small left>mdi-magnify</v-icon>
            검색하기
          </v-btn>
        </v-sheet>
        <v-spacer></v-spacer>
        <v-btn height="40" class="px-3" color="primary" dark depressed @click="refresh()">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-sheet>

      <!-- 목록 -->
      <v-data-table
        :headers="headers"
        :items="list"
        :server-items-length="total"
        :options.sync="options"
        :loading="loading"
        :footer-props="{
          'items-per-page-options': [10, 20, 50, -1],
          itemsPerPageText: '페이지당 항목 수',
        }"
        no-data-text="등록된 신청이 없습니다"
        @click:row="openDetail"
        class="row-pointer"
      >
        <template v-slot:item.created="{ item }">
          {{ formatDate(item.created) }}
        </template>
        <template v-slot:item.requested_day="{ item }">
          {{ item.requested_day ? item.requested_day.slice(0, 10) : "" }}
        </template>
        <template v-slot:item.participant_category="{ item }">
          <v-chip small :color="item.participant_category === 'japanese' ? 'orange' : 'blue'" dark>
            {{ item.participant_category === "japanese" ? "일본" : "한국" }}
          </v-chip>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip small :color="statusColor(item.status)" dark>
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>
      </v-data-table>
    </v-sheet>

    <!-- 상세 / 수정 다이얼로그 -->
    <v-dialog v-model="dialog" max-width="640" scrollable>
      <v-card v-if="selected">
        <v-card-title class="d-flex justify-space-between">
          <span>신청 상세 / 수정</span>
          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pt-4">
          <!-- 읽기 전용 정보 -->
          <v-simple-table class="mb-4">
            <tbody>
              <tr>
                <td class="font-weight-bold" width="130">신청일</td>
                <td>{{ formatDate(selected.created) }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">국적</td>
                <td>
                  <v-chip small :color="selected.participant_category === 'japanese' ? 'orange' : 'blue'" dark>
                    {{ selected.participant_category === "japanese" ? "일본" : "한국" }}
                  </v-chip>
                </td>
              </tr>
              <tr>
                <td class="font-weight-bold">희망 날짜</td>
                <td>{{ selected.requested_day ? selected.requested_day.slice(0, 10) : "-" }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">이메일</td>
                <td>{{ selected.contact_email }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">메신저</td>
                <td>{{ selected.contact_messenger_type }} · {{ selected.contact_messenger }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">동의 여부</td>
                <td>리뷰: {{ selected.consent_review_upload ? "동의" : "미동의" }} / 사진: {{ selected.consent_photo_use ? "동의" : "미동의" }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-divider class="mb-4"></v-divider>

          <!-- 수정 가능 필드 -->
          <p class="font-weight-bold mb-3 grey--text text--darken-2">수정 항목</p>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field v-model="form.display_name" label="이름 / 닉네임" outlined dense hide-details></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="form.contact_email" label="이메일" outlined dense hide-details></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model.number="form.party_size" label="방문 인원" outlined dense hide-details type="number" min="1"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select v-model="form.status" :items="statusOptions" item-text="label" item-value="value" label="상태" outlined dense hide-details></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-menu v-model="confirmedDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.confirmed_date"
                    label="확정 날짜"
                    outlined
                    dense
                    hide-details
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    clearable
                    @click:clear="form.confirmed_date = ''"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="form.confirmed_date" no-title @input="confirmedDateMenu = false"></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-4 d-flex justify-space-between">
          <v-btn color="red" dark depressed @click="deleteItem(selected)">
            <v-icon small left>mdi-delete-outline</v-icon>
            삭제
          </v-btn>
          <div>
            <v-btn text class="mr-2" @click="dialog = false">취소</v-btn>
            <v-btn color="primary" depressed :loading="saving" @click="save()">
              <v-icon small left>mdi-content-save-outline</v-icon>
              저장
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  data: () => ({
    keyword: "",
    list: [],
    total: 0,
    loading: false,
    saving: false,
    options: {},

    headers: [
      { text: "신청일", value: "created", width: "150" },
      { text: "희망 날짜", value: "requested_day", width: "120" },
      { text: "이름", value: "display_name", width: "120" },
      { text: "국적", value: "participant_category", width: "90" },
      { text: "방문 인원", value: "party_size", width: "90" },
      { text: "상태", value: "status", width: "120" },
    ],

    statusOptions: [
      { value: "pending_review", label: "검토 중" },
      { value: "confirmed", label: "확정" },
      { value: "waitlist", label: "대기" },
      { value: "cancelled", label: "취소" },
    ],

    dialog: false,
    selected: null,
    form: {},
    confirmedDateMenu: false,
  }),

  watch: {
    options: {
      handler() {
        this.load()
      },
      deep: true,
    },
  },

  methods: {
    load() {
      this.loading = true
      const { page, itemsPerPage } = this.options
      this.$http
        .post("/api/budokai/select", {
          params: {
            page: page || 1,
            itemsPerPage: itemsPerPage || 10,
            keyword: this.keyword || undefined,
          },
        })
        .then((res) => {
          this.list = res.data.list || []
          this.total = res.data.total || 0
        })
        .catch((err) => console.error(err))
        .finally(() => {
          this.loading = false
        })
    },

    search() {
      this.options.page = 1
      this.load()
    },

    refresh() {
      this.keyword = ""
      this.options.page = 1
      this.load()
    },

    openDetail(item) {
      this.selected = item
      this.form = {
        display_name: item.display_name,
        contact_email: item.contact_email,
        party_size: item.party_size,
        status: item.status,
        confirmed_date: item.confirmed_date ? item.confirmed_date.slice(0, 10) : "",
      }
      this.dialog = true
    },

    save() {
      if (!this.selected) return
      this.saving = true

      const operatorId = this.$store.state.lafia_user.user_id || null

      this.$http
        .post("/api/budokai/update", {
          params: {
            id: this.selected.id,
            display_name: this.form.display_name,
            contact_email: this.form.contact_email,
            requested_day: this.form.requested_day || null,
            party_size: this.form.party_size,
            status: this.form.status,
            confirmed_date: this.form.confirmed_date || null,
            operator_id: operatorId,
          },
        })
        .then((res) => {
          if (res.data.success) {
            this.dialog = false
            this.load()
          }
        })
        .catch((err) => console.error(err))
        .finally(() => {
          this.saving = false
        })
    },

    deleteItem(item) {
      if (!confirm("정말 해당 신청을 삭제하겠습니까?")) return
      this.$http
        .post("/api/budokai/delete", { params: { id: item.id } })
        .then((res) => {
          if (res.data.affectedRows) {
            this.dialog = false
            this.selected = null
            this.load()
          }
        })
        .catch((err) => console.error(err))
    },

    statusColor(status) {
      const map = { confirmed: "green", waitlist: "orange", cancelled: "red", pending_review: "blue-grey" }
      return map[status] || "grey"
    },

    statusLabel(status) {
      const map = { confirmed: "확정", waitlist: "대기", cancelled: "취소", pending_review: "검토 중" }
      return map[status] || status
    },

    formatDate(dateStr) {
      if (!dateStr) return ""
      const d = new Date(dateStr)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
    },
  },
}
</script>

<style scoped>
.row-pointer >>> tbody tr {
  cursor: pointer;
}
.row-pointer >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}
</style>
